// Domaine Échanges : Coaching (calendrier, consultations, Team Genius, bilans, équipe), Communauté, Messages, Boutique,
// Compte et Miles. Composants réels de ubm-ui-components ; données dans ./exchanges-fixtures ; état local partagé par un
// petit store (les contenus ouverts dans le sidepeek du shell s’y abonnent aussi). Aucun réseau, aucun envoi, aucun paiement :
// chaque action engageante est simulée et le dit.
import { useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from "react";
import { Heart, MessageCircle, SquarePen, Newspaper, CalendarDays, Trophy, Sparkles, Lightbulb, TrendingUp } from "lucide-react";
import type { PageProps, Route } from "./contracts";
import { Composer, setPageChrome } from "./shared";
import { eventVisual } from "./event-visuals";
import { Alert } from "../vendor/ubm-ui-components/src/components/alert";
import { Avatar } from "../vendor/ubm-ui-components/src/components/avatar";
import { Badge, type BadgeTone } from "../vendor/ubm-ui-components/src/components/badge";
import { Button } from "../vendor/ubm-ui-components/src/components/button";
import { Card, CardSurface } from "../vendor/ubm-ui-components/src/components/card";
import { ChatThread, type ChatMessage } from "../vendor/ubm-ui-components/src/components/chat-thread";
import { MetricCard } from "../vendor/ubm-ui-components/src/components/metric-card";
import { RadioCards } from "../vendor/ubm-ui-components/src/components/radio-cards";
import { SegmentedControl } from "../vendor/ubm-ui-components/src/components/segmented-control";
import { Switch } from "../vendor/ubm-ui-components/src/components/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../vendor/ubm-ui-components/src/components/tabs";
import { Textarea } from "../vendor/ubm-ui-components/src/components/textarea";
import { Timeline, type TimelineEvent } from "../vendor/ubm-ui-components/src/components/timeline";
import { AvailabilityPicker, type AvailabilityDay } from "../vendor/ubm-ui-components/src/blocks/availability-picker";
import { WeekCalendar, type CalendarEvent } from "../vendor/ubm-ui-components/src/blocks/week-calendar";
import {
  ACCOUNT_NOTE, AGENDA_ACCESS_LABEL, AGENDA_EVENTS, AGENDA_SOURCES, AGENDA_UNDATED, BILAN_CURRENT, BILAN_HISTORY, BILAN_QUESTIONS, BILAN_VERSION,
  BOOKING_HORIZON_DAYS, COIN_LABEL, COMMUNITY_AUTHORS, CONSULT_POLICY, DEMO_NOW, DEMO_TODAY, DAYS_SHORT_FR, GENIUS_CATCHUP_POLICY, GENIUS_NEXT, GENIUS_PAST,
  GENIUS_QUESTIONS, INITIAL_ACQUISITIONS, INITIAL_APPOINTMENTS, INITIAL_BALANCES, INITIAL_MEMBERSHIP, INITIAL_NOTIFICATIONS, INITIAL_POSTS, INITIAL_REQUESTS,
  INITIAL_THREADS, MEMBER, MEMBERSHIP, MILES_JOURNAL, MILES_NOTE, NOTIFICATION_PREFS, OFFERS, PAY_LABEL, PINNED_POST_ID, SERVICES, SHOP_CATEGORIES, SLOT_RULES,
  SPACES, SPACE_PLACEHOLDER, TEAM_AUTHOR, TEAM_PEOPLE, TIME_ZONE_LABEL,
  addDaysIso, addMinutes, asset, at, capitalize, diffDays, hourLabel, longDate, parseIso, serviceById,
  type Acquisition, type AgendaItem, type Appointment, type CoinKind, type CommunityComment, type CommunityPost, type GeniusQuestion, type GeniusSession,
  type Offer, type OfferCover, type PayMethod, type PaymentKind, type QuoteRequest, type ServiceId, type TeamThread,
} from "./exchanges-fixtures";
import type { CoachingSnapshot, CoachingStore } from "./coaching";
import "./exchanges.css";

// ── Store local du domaine ───────────────────────────────────────────────────

type CoachingTab = "calendar" | "consult" | "genius" | "bilans" | "team";
interface ExchangeState {
  coachingTab: CoachingTab;
  balances: Record<CoinKind, number>;
  appointments: Appointment[];
  seq: number;
  geniusQuestions: GeniusQuestion[];
  geniusLeads: Record<string, boolean>;
  geniusCatchup: Record<string, string>;
  bilanAnswers: Record<string, string>;
  bilanSent: string | null;
  posts: CommunityPost[];
  /** Publications ouvertes par le membre. Amorcé vide : toute publication existante d’un autre auteur reste non lue jusqu’à son ouverture. */
  readPosts: Record<string, boolean>;
  membership: Record<string, boolean>;
  pinnedClosed: boolean;
  reacted: Record<string, boolean>;
  threads: TeamThread[];
  activeThread: string;
  acquisitions: Acquisition[];
  requests: QuoteRequest[];
  notifications: Record<string, boolean>;
  /** Member identity edited from Mon compte (seeded from MEMBER). */
  profile: { name: string; email: string };
}
const initialState = (): ExchangeState => ({
  profile: { name: MEMBER.name, email: MEMBER.email },
  coachingTab: "calendar",
  balances: { ...INITIAL_BALANCES },
  appointments: INITIAL_APPOINTMENTS.map(a => ({ ...a, history: [...a.history] })),
  seq: 1,
  geniusQuestions: [...GENIUS_QUESTIONS],
  geniusLeads: {},
  geniusCatchup: {},
  bilanAnswers: {},
  bilanSent: null,
  posts: INITIAL_POSTS,
  readPosts: {},
  membership: { ...INITIAL_MEMBERSHIP },
  pinnedClosed: false,
  reacted: {},
  threads: INITIAL_THREADS,
  activeThread: INITIAL_THREADS[0]?.id ?? "",
  acquisitions: [...INITIAL_ACQUISITIONS],
  requests: [...INITIAL_REQUESTS],
  notifications: { ...INITIAL_NOTIFICATIONS },
});

let state = initialState();
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
const getState = () => state;
function update(fn: (s: ExchangeState) => ExchangeState) { state = fn(state); listeners.forEach(l => l()); }
const useExchange = () => useSyncExternalStore(subscribe, getState, getState);

// ── Actions d’accompagnement (une seule logique, partagée par les écrans et l’adaptateur CoachingStore) ──

function bookAppointment({ service, date, time, prep, kind }: { service: ServiceId; date: string; time: string; prep: string; kind: PaymentKind }) {
  const sv = serviceById(service);
  update(st => {
    if (kind === "credit" && st.balances[sv.coin] < 1) return st;
    const appointment: Appointment = { id: "b" + st.seq, service, date, time, payment: kind === "credit" ? { kind } : { kind, amount: sv.price },
      status: "confirmed", link: false, prep: prep.trim(), history: [], reschedules: 0 };
    return { ...st, seq: st.seq + 1, appointments: [...st.appointments, appointment],
      balances: kind === "credit" ? { ...st.balances, [sv.coin]: st.balances[sv.coin] - 1 } : st.balances };
  });
}
function rescheduleAppointment(id: string, date: string, time: string) {
  update(st => ({ ...st, appointments: st.appointments.map(a => a.id === id ? { ...a, date, time, link: false, reschedules: a.reschedules + 1,
    history: [...a.history, `Reporté depuis le ${longDate(a.date)} à ${hourLabel(a.time)}, sans nouveau débit`] } : a) }));
}
function cancelAppointment(id: string) {
  update(st => {
    const a = st.appointments.find(x => x.id === id);
    if (!a || a.status !== "confirmed") return st;
    const sv = serviceById(a.service);
    return { ...st,
      balances: a.payment.kind === "credit" ? { ...st.balances, [sv.coin]: st.balances[sv.coin] + 1 } : st.balances,
      appointments: st.appointments.map(x => x.id === id ? { ...x, status: "cancelled", history: [...x.history,
        a.payment.kind === "credit" ? `1 ${sv.coinName} restituée à l’annulation` : `Remboursement bancaire de ${a.payment.amount} € à traiter séparément`] } : x) };
  });
}
const toggleLead = (id: string) => update(st => ({ ...st, geniusLeads: { ...st.geniusLeads, [id]: !st.geniusLeads[id] } }));
// ── Actions du compte (une seule logique, partagée par les écrans Boutique/Compte et la façade exchangeAccountSource) ──

function acquireOffer({ offerId, pay }: { offerId: string; pay: PayMethod }) {
  const o = OFFERS.find(x => x.id === offerId);
  if (!o || o.mode !== "buy" || !o.pay?.includes(pay)) throw new Error("Cette offre ne peut pas être acquise avec ce règlement.");
  if (state.acquisitions.some(a => a.offer === offerId)) return;
  update(st => ({ ...st, acquisitions: [...st.acquisitions, { offer: o.id, date: DEMO_TODAY, pay, state: o.category === "services" ? "running" : "access" }] }));
}
function requestQuote({ offerId, text }: { offerId: string; text: string }) {
  const body = text.trim();
  const o = OFFERS.find(x => x.id === offerId);
  if (!o || o.mode !== "quote" || !body) throw new Error("La demande n’a pas pu être envoyée.");
  if (state.requests.some(r => r.offer === offerId)) return;
  update(st => ({ ...st, requests: [...st.requests, { offer: o.id, date: DEMO_TODAY, text: body }] }));
}
const setNotification = (id: string, enabled: boolean) => update(st => ({ ...st, notifications: { ...st.notifications, [id]: enabled } }));
function updateProfile(patch: { name?: string; email?: string }) {
  const name = patch.name?.trim(), email = patch.email?.trim();
  if (name === "" || (email !== undefined && !/.+@.+\..+/.test(email))) throw new Error("Nom ou e-mail incomplet.");
  update(st => ({ ...st, profile: { name: name ?? st.profile.name, email: email ?? st.profile.email } }));
}

/** Account facade on the Échanges store: profile, credits, acquisitions, requests and notification preferences of the
 *  same store as Coaching/Boutique (no copy). `getState` keeps the same identity while those slices are unchanged. */
export interface ExchangeAccountState {
  profile: { name: string; email: string; status: string; program: string };
  balances: ExchangeState["balances"]; acquisitions: readonly Acquisition[]; requests: readonly QuoteRequest[]; notifications: Record<string, boolean>;
}
let accountSlice: ExchangeAccountState | null = null;
let accountSliceProfile: ExchangeState["profile"] | null = null;
export const exchangeAccountSource = {
  subscribe,
  getState: (): ExchangeAccountState => {
    const s = state, c = accountSlice;
    if (!c || accountSliceProfile !== s.profile || c.balances !== s.balances || c.acquisitions !== s.acquisitions || c.requests !== s.requests || c.notifications !== s.notifications) {
      accountSliceProfile = s.profile;
      accountSlice = { profile: { ...s.profile, status: MEMBERSHIP.label, program: MEMBERSHIP.program },
        balances: s.balances, acquisitions: s.acquisitions, requests: s.requests, notifications: s.notifications };
    }
    return accountSlice!;
  },
  actions: { acquireOffer, requestQuote, setNotification, updateProfile },
};

const requestCatchup = (sessionId: string, reason: string) => update(st => ({ ...st, geniusCatchup: { ...st.geniusCatchup, [sessionId]: reason.trim() } }));
function askQuestion(text: string) {
  const body = text.trim(); if (!body) return;
  update(st => ({ ...st, seq: st.seq + 1, geniusQuestions: [...st.geniusQuestions, { id: "mq" + st.seq, author: MEMBER.name, time: "à l’instant", text: body }] }));
}

/** Adaptateur du store Échanges vers le contrat CoachingStore (./coaching) : mêmes soldes, rendez-vous, pistes, rattrapages et
 *  questions que les écrans Échanges. La racine le branche avec connectCoachingStore(exchangeCoachingStore). Simulé : ce navigateur seul. */
let coachingSnap: CoachingSnapshot | null = null;
export const exchangeCoachingStore: CoachingStore = {
  simulated: true,
  subscribe,
  getSnapshot: () => {
    const s = state, c = coachingSnap;
    if (!c || c.balances !== s.balances || c.appointments !== s.appointments || c.leads !== s.geniusLeads || c.catchups !== s.geniusCatchup || c.questions !== s.geniusQuestions)
      coachingSnap = { balances: s.balances, appointments: s.appointments, leads: s.geniusLeads, catchups: s.geniusCatchup, questions: s.geniusQuestions };
    return coachingSnap!;
  },
  actions: {
    confirmBooking: input => bookAppointment({ ...input, kind: "credit" }),
    rescheduleAppointment, cancelAppointment, toggleLead, requestCatchup, askQuestion,
  },
};

function useNarrow(query = "(max-width: 760px)") {
  const get = () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(query).matches;
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(query);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return narrow;
}

// ── Petits assemblages de présentation (domaine) ─────────────────────────────

function Section({ title, aside, children, className }: { title?: string; aside?: ReactNode; children: ReactNode; className?: string }) {
  return <section className={["ex-section", className].filter(Boolean).join(" ")}>
    {title || aside ? <div className="ex-section-head">{title ? <h2 className="ex-h2">{title}</h2> : <span />}{aside}</div> : null}
    {children}
  </section>;
}

function Facts({ rows }: { rows: [string, ReactNode][] }) {
  return <dl className="ex-facts">{rows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>;
}

function Coin({ kind, size = "md" }: { kind: CoinKind; size?: "md" | "lg" }) {
  return <span className={`ex-coin ex-coin-${kind} ex-coin-${size}`} role="img" aria-label={COIN_LABEL[kind]} />;
}

const whenLabel = (a: { date: string; time: string; service: ServiceId }) =>
  `${capitalize(longDate(a.date))} · ${hourLabel(a.time)}–${hourLabel(addMinutes(a.time, serviceById(a.service).duration))}`;
const paymentLabel = (a: Appointment) => a.payment.kind === "credit" ? `1 ${serviceById(a.service).coinName}` : `Carte · ${a.payment.amount} €`;
const statusLabel = (a: Appointment) => a.status === "cancelled" ? "Annulé" : a.status === "done" ? "Passé" : "Confirmé";
const statusTone = (a: Appointment): BadgeTone => a.status === "cancelled" ? "neutral" : a.status === "done" ? "neutral" : "success";
const isUpcoming = (a: Appointment) => a.status === "confirmed" && diffDays(a.date) >= 0;

// ── Créneaux et réservation ──────────────────────────────────────────────────

function slotsFor(service: ServiceId, iso: string, appointments: Appointment[]) {
  const rule = SLOT_RULES[service], n = diffDays(iso);
  if (n < 0 || !rule.weekdays.includes(parseIso(iso).getDay())) return [];
  const times = n === 0 ? rule.todayTimes ?? [] : rule.times;
  return times
    .filter(t => !appointments.some(a => a.status === "confirmed" && a.service === service && a.date === iso && a.time === t))
    .map(t => ({ time: t, places: rule.places?.(n) }));
}

function availabilityDays(service: ServiceId, appointments: Appointment[]): AvailabilityDay[] {
  const duration = serviceById(service).duration;
  return Array.from({ length: BOOKING_HORIZON_DAYS }, (_, i) => addDaysIso(DEMO_TODAY, i))
    .map(iso => ({ iso, slots: slotsFor(service, iso, appointments) }))
    .filter(d => d.slots.length > 0)
    .map(({ iso, slots }) => {
      const d = parseIso(iso);
      return {
        id: iso, weekday: diffDays(iso) === 0 ? "auj." : DAYS_SHORT_FR[d.getDay()], day: String(d.getDate()), label: capitalize(longDate(iso)),
        slots: slots.map(s => ({ id: s.time, start: at(iso, s.time), end: at(iso, addMinutes(s.time, duration)), label: hourLabel(s.time) + (s.places ? ` · ${s.places} places` : "") })),
      };
    });
}

const PICKER_STRINGS = {
  days: "Jours", times: (day: string) => `Créneaux du ${day}`, busy: "complet", none: "Plus de créneau ce jour-là.", choose: "Choisis un créneau",
  selected: (day: string, time: string) => `${day} à ${time}`, confirm: "Continuer", confirming: "Vérification…", confirmed: (day: string, time: string) => `Créneau retenu : ${day} à ${time}.`,
  failed: "Ce créneau n’a pas pu être retenu. Ton choix est conservé.", retry: "Réessayer", unavailable: "Réservation indisponible ici.",
  taken: "Ce créneau n’est plus disponible. Choisis-en un autre.", attendees: "Intervenant",
};

type BookingStep = "slot" | "pay" | "recap" | "paying" | "done";
function BookingFlow({ serviceId, rescheduleOf }: { serviceId: ServiceId; rescheduleOf?: string }) {
  const s = useExchange();
  const service = serviceById(serviceId);
  const original = rescheduleOf ? s.appointments.find(a => a.id === rescheduleOf) : undefined;
  const [step, setStep] = useState<BookingStep>("slot");
  const [slot, setSlot] = useState<{ date: string; time: string } | null>(null);
  const [pay, setPay] = useState<PaymentKind | null>(null);
  const [prep, setPrep] = useState("");
  const [notice, setNotice] = useState("");
  const [balanceBefore, setBalanceBefore] = useState(0);
  const balance = s.balances[service.coin];
  const days = useMemo(() => availabilityDays(serviceId, s.appointments), [serviceId, s.appointments]);
  const attendees = service.who === "Arnaud" ? [{ name: "Arnaud", avatar: asset("tc-arnaud-a-propos.jpg") }] : [];

  const commit = (kind: PaymentKind) => {
    if (!slot) return;
    setBalanceBefore(balance);
    bookAppointment({ service: serviceId, date: slot.date, time: slot.time, prep, kind });
    setStep("done");
  };
  const commitReschedule = () => {
    if (!slot || !original) return;
    rescheduleAppointment(original.id, slot.date, slot.time);
    setStep("done");
  };

  if (step === "done" && slot) {
    return <div className="ex-stack">
      <Alert tone="success" title={original ? "Ton rendez-vous est déplacé" : "Ton rendez-vous est confirmé"}>
        {service.name} · {capitalize(longDate(slot.date))} · {hourLabel(slot.time)} · {TIME_ZONE_LABEL}
      </Alert>
      <p className="ex-muted">{original ? "Règlement initial conservé, aucun nouveau débit." : pay === "credit"
        ? `1 ${service.coinName} utilisée · solde ${balanceBefore} → ${balanceBefore - 1}.`
        : `${service.price} € par carte · pièces inchangées.`}</p>
      <p className="ex-muted">Le rendez-vous apparaît dans ton calendrier et dans « Mes rendez-vous ».</p>
    </div>;
  }

  return <div className="ex-stack">
    <p className="ex-kicker">{original ? "Report" : "Réservation"} · {step === "slot" ? "Créneau" : step === "pay" ? "Règlement" : step === "paying" ? "Paiement" : "Vérification"}</p>
    {notice ? <Alert tone="info" title="Rien n’a été débité" onDismiss={() => setNotice("")}>{notice}</Alert> : null}

    {step === "slot" ? <>
      <p className="ex-muted">{service.mode} · {service.duration} min · {TIME_ZONE_LABEL}</p>
      {days.length ? <AvailabilityPicker title={service.name} duration={`${service.duration} min`} timeZoneLabel={TIME_ZONE_LABEL} attendees={attendees} days={days}
        strings={PICKER_STRINGS} defaultValue={slot ? { dayId: slot.date, slotId: slot.time } : null}
        onConfirm={sel => { setSlot({ date: sel.dayId, time: sel.slotId }); setStep(original ? "recap" : "pay"); }} />
        : <Alert tone="info" title="Aucun créneau sur les deux prochaines semaines">L’équipe ouvre de nouveaux créneaux régulièrement. Tu peux lui écrire depuis Messages.</Alert>}
    </> : null}

    {step === "pay" ? <>
      <RadioCards aria-label="Règlement" value={pay} onValueChange={v => setPay(v as PaymentKind)} options={[
        { value: "credit", label: `1 ${service.coinName}`, description: balance >= 1 ? `Solde : ${balance} → ${balance - 1}` : undefined, icon: <Coin kind={service.coin} />,
          disabled: balance < 1, disabledReason: `Aucune ${service.coinName} disponible. Chaque pièce vaut pour sa catégorie.` },
        { value: "card", label: `${service.price} € par carte` },
      ]} />
      <div className="ex-actions">
        <Button variant="ghost" onClick={() => setStep("slot")}>Changer de créneau</Button>
        <Button disabled={!pay} onClick={() => setStep("recap")}>Continuer</Button>
      </div>
    </> : null}

    {step === "recap" && slot ? <>
      <Facts rows={[
        ["Prestation", service.name], ["Date", capitalize(longDate(slot.date))], ["Heure", `${hourLabel(slot.time)}–${hourLabel(addMinutes(slot.time, service.duration))} · ${TIME_ZONE_LABEL}`],
        ["Intervenant", service.who], ["Modalité", service.mode],
        ["Règlement", original ? `Conservé : ${paymentLabel(original)} · aucun nouveau débit` : pay === "credit" ? `1 ${service.coinName} · solde ${balance} → ${balance - 1}` : `${service.price} € par carte · pièces inchangées`],
      ]} />
      <p className="ex-small">{CONSULT_POLICY}</p>
      {!original ? <Textarea label="Ta question pour la séance (facultatif)" rows={2} value={prep} onChange={e => setPrep(e.target.value)} placeholder="Ce que tu veux travailler…" /> : null}
      <div className="ex-actions">
        <Button variant="ghost" onClick={() => setStep(original ? "slot" : "pay")}>Retour</Button>
        {original ? <Button onClick={commitReschedule}>Confirmer le report</Button>
          : pay === "credit" ? <Button onClick={() => commit("credit")}>Confirmer et utiliser 1 {service.coinName}</Button>
          : <Button onClick={() => setStep("paying")}>Continuer vers le paiement · {service.price} €</Button>}
      </div>
    </> : null}

    {step === "paying" ? <>      <div className="ex-actions">
        <Button variant="secondary" onClick={() => { setStep("recap"); setNotice("Paiement abandonné. Aucun rendez-vous n’a été créé. Ta sélection est conservée."); }}>Abandonner le paiement</Button>
        <Button onClick={() => commit("card")}>Confirmer le paiement</Button>
      </div>
    </> : null}
  </div>;
}

function AppointmentDetail({ id }: { id: string }) {
  const s = useExchange();
  const a = s.appointments.find(x => x.id === id);
  const [mode, setMode] = useState<"view" | "reschedule" | "cancel">("view");
  const [opened, setOpened] = useState(false);
  if (!a) return <p className="ex-muted">Ce rendez-vous n’existe plus.</p>;
  const service = serviceById(a.service), up = isUpcoming(a);
  if (mode === "reschedule") return <BookingFlow serviceId={a.service} rescheduleOf={a.id} />;
  const cancel = () => { cancelAppointment(a.id); setMode("view"); };
  return <div className="ex-stack">
    <img className="ex-event-visual" src={eventVisual("consult")} alt="" />
    <div className="ex-row"><Coin kind={service.coin} size="lg" /><Badge tone={statusTone(a)}>{statusLabel(a)}</Badge></div>
    <p className="ex-lead">{whenLabel(a)}</p>
    <p className="ex-muted">{TIME_ZONE_LABEL}</p>
    {up ? (a.link
      ? <div className="ex-stack-sm"><Button onClick={() => setOpened(true)}>Rejoindre la visio</Button></div>
      : <CardSurface variant="outline" padding="sm" className="ex-inline-note"><span>Lien de visio pas encore disponible. Il apparaîtra ici dès qu’il sera communiqué.</span>
          <Button size="sm" variant="secondary" onClick={() => update(st => ({ ...st, appointments: st.appointments.map(x => x.id === a.id ? { ...x, link: true } : x) }))}>Vérifier</Button></CardSurface>) : null}
    <Facts rows={[["Durée", `${service.duration} min`], ["Intervenant", service.who], ["Modalité", service.mode],
      ["Règlement", paymentLabel(a) + (a.payment.kind === "credit" ? " · utilisée à la réservation" : "")]]} />
    {a.prep ? <Section title="Ta question pour la séance"><p>{a.prep}</p></Section> : null}
    {a.history.length ? <ul className="ex-history">{a.history.map(h => <li key={h}>{h}</li>)}</ul> : null}
    {a.status === "done" ? <div className="ex-stack-sm">
      <p className="ex-muted">{a.replay ? "Replay disponible depuis la fiche de la séance." : "Pas de replay pour cette consultation."}</p>
      {a.action ? <CardSurface variant="sunken" padding="sm"><p className="ex-small">Action issue de cette consultation</p><p className="ex-strong">{a.action}</p></CardSurface> : null}
    </div> : null}
    {up && mode === "view" ? <div className="ex-stack-sm">
      <p className="ex-small">{CONSULT_POLICY}{a.reschedules >= 1 ? " Le report de ce rendez-vous a déjà été utilisé." : ""}</p>
      <div className="ex-actions">
        <Button variant="secondary" disabled={a.reschedules >= 1} onClick={() => setMode("reschedule")}>Reporter</Button>
        <Button variant="ghost" onClick={() => setMode("cancel")}>Annuler le rendez-vous</Button>
      </div>
    </div> : null}
    {mode === "cancel" ? <Alert tone="warning" title="Annuler ce rendez-vous ?">
      <p>{a.payment.kind === "credit" ? `Ta ${service.coinName} est restituée : solde ${s.balances[service.coin]} → ${s.balances[service.coin] + 1}.`
        : `Ton paiement de ${a.payment.amount} € relève d’un remboursement bancaire distinct.`}</p>
      <div className="ex-actions"><Button variant="ghost" onClick={() => setMode("view")}>Garder le rendez-vous</Button><Button variant="danger" onClick={cancel}>Annuler le rendez-vous</Button></div>
    </Alert> : null}
  </div>;
}

// ── Coaching ─────────────────────────────────────────────────────────────────

const CALENDAR_STRINGS = {
  today: "Aujourd’hui", previous: "Précédent", next: "Suivant", views: "Vue",
  viewNames: { week: "Semaine", workweek: "Semaine de travail", day: "Jour", "3day": "3 jours", agenda: "Liste" },
  calendars: "Calendriers", newEvent: "Nouvel événement", allDay: "Journée", title: "Titre", untitled: "Sans titre", date: "Date", start: "Début", end: "Fin",
  calendar: "Calendrier", location: "Lieu", save: "Enregistrer", cancel: "Annuler", delete: "Supprimer", undo: "Défaire", noEvents: "Aucun événement",
  more: (n: number) => `+${n} de plus`, createUnavailable: "Les événements viennent de ton accompagnement : rien à créer ici.",
  updateUnavailable: "Les événements se gèrent depuis leur fiche.", deleteUnavailable: "Les événements se gèrent depuis leur fiche.",
  failed: "Action non aboutie. Rien n’a changé.", invalidRange: "La fin doit suivre le début.",
  instructions: "Les flèches parcourent les jours. Entrée ouvre un événement. T revient à aujourd’hui, D et W changent de vue.",
  gridLabel: (range: string) => `Calendrier, ${range}`,
  cellLabel: (date: string, count: number) => `${date}, ${count === 0 ? "aucun événement" : count === 1 ? "1 événement" : `${count} événements`}`,
};

interface PlannedItem { id: string; source: AgendaItem["source"]; title: string; date: string; start: string; end: string; place: string; badge: string; appointment?: string; item?: AgendaItem }
function plannedItems(appointments: Appointment[]): PlannedItem[] {
  const own = appointments.filter(a => a.status !== "cancelled").map(a => {
    const sv = serviceById(a.service);
    return { id: a.id, source: "consult" as const, title: sv.collective ? sv.name : `Consultation ${sv.name === "Arnaud" ? "avec Arnaud" : sv.name}`, date: a.date, start: a.time,
      end: addMinutes(a.time, sv.duration), place: "Visio", badge: a.status === "done" ? "Passé" : "Réservé", appointment: a.id };
  });
  const events = AGENDA_EVENTS.map(e => ({ id: e.id, source: e.source, title: e.title, date: e.date, start: e.start, end: e.end, place: e.place, badge: AGENDA_ACCESS_LABEL[e.access], item: e }));
  return [...own, ...events].sort((x, y) => (x.date + x.start < y.date + y.start ? -1 : 1));
}

function AgendaEventDetail({ item, onBook, onNavigate }: { item: AgendaItem; onBook: (service: ServiceId) => void; onNavigate: (route: Route) => void }) {
  return <div className="ex-stack">
    <img className="ex-event-visual" src={eventVisual(item.source)} alt="" />
    <div className="ex-row"><Badge tone="accent">{AGENDA_SOURCES.find(s => s.id === item.source)?.name}</Badge><Badge>{AGENDA_ACCESS_LABEL[item.access]}</Badge></div>
    <p className="ex-lead">{capitalize(longDate(item.date))} · {hourLabel(item.start)}–{hourLabel(item.end)}</p>
    <p>{item.desc}</p>
    <Facts rows={[["Lieu", item.place] as [string, string], ...(item.where ? [["Accès", item.where] as [string, string]] : []), ...(item.org ? [["Organisé par", item.org] as [string, string]] : [])]} />
    {item.access === "register" ? <p className="ex-small">L’inscription aux étapes de la Tournée se fait auprès de l’organisateur.</p> : null}
    {item.next ? <div className="ex-actions">
      <Button onClick={() => item.next?.service ? onBook(item.next.service) : onNavigate(item.next!.route)}>{item.next.label}</Button>
    </div> : null}
  </div>;
}

function CalendarTab({ openPeek, navigate, book }: PageProps & { book: (service: ServiceId) => void }) {
  const s = useExchange();
  const narrow = useNarrow();
  const items = useMemo(() => plannedItems(s.appointments), [s.appointments]);
  const events: CalendarEvent[] = useMemo(() => items.map(i => ({ id: i.id, title: i.title, calendarId: i.source, startDate: at(i.date, i.start), endDate: at(i.date, i.end),
    location: i.place })), [items]);
  const upcoming = items.filter(i => diffDays(i.date) >= 0).slice(0, 6);
  const open = (i: PlannedItem) => i.appointment
    ? openPeek({ title: i.title, content: <AppointmentDetail id={i.appointment} /> })
    : openPeek({ title: i.title, content: <AgendaEventDetail item={i.item!} onBook={book} onNavigate={navigate} /> });
  return <div className="ex-calendar-layout">
    <div className="ex-calendar">
      <WeekCalendar key={narrow ? "agenda" : "week"} events={events} calendars={AGENDA_SOURCES} defaultDate={parseIso("2026-10-05")} defaultView={narrow ? "agenda" : "week"}
        weekStartsOn={1} startHour={8} endHour={22} timeZone="Europe/Paris" locale="fr-FR" now={DEMO_NOW} strings={CALENDAR_STRINGS} />
    </div>
    <Section title="À venir" className="ex-calendar-aside">
      <ul className="ex-list">{upcoming.map(i => <li key={i.id}>
        <button type="button" className="ex-row-button" onClick={() => open(i)}>
          <img className="ex-event-thumb" src={eventVisual(i.source)} alt="" loading="lazy" />
          <span className="ex-row-text"><b>{i.title}</b><span>{DAYS_SHORT_FR[parseIso(i.date).getDay()]} {parseIso(i.date).getDate()} · {hourLabel(i.start)} · {i.place}</span></span>
          <Badge size="sm" tone={i.badge === "Réservé" ? "success" : "neutral"}>{i.badge}</Badge>
        </button>
      </li>)}</ul>
      {AGENDA_UNDATED.map(u => <p key={u.id} className="ex-small"><b>{u.title}</b> · {u.desc}</p>)}
    </Section>
  </div>;
}

function ConsultTab({ openPeek, book }: PageProps & { book: (service: ServiceId) => void }) {
  const s = useExchange();
  const upcoming = s.appointments.filter(isUpcoming).sort((x, y) => (x.date + x.time < y.date + y.time ? -1 : 1));
  const past = s.appointments.filter(a => !isUpcoming(a)).sort((x, y) => (x.date < y.date ? 1 : -1));
  const next = upcoming[0];
  const openAppt = (a: Appointment) => openPeek({ title: serviceById(a.service).name, content: <AppointmentDetail id={a.id} /> });
  // Upcoming consultations carry the shared consultation visual; past/cancelled rows keep their coin.
  const row = (a: Appointment) => <li key={a.id}><button type="button" className="ex-row-button" onClick={() => openAppt(a)}>
    {isUpcoming(a) ? <img className="ex-event-thumb" src={eventVisual("consult")} alt="" loading="lazy" /> : <Coin kind={serviceById(a.service).coin} />}
    <span className="ex-row-text"><b>{serviceById(a.service).name}</b><span>{whenLabel(a)}</span></span>
    <Badge size="sm" tone={statusTone(a)}>{statusLabel(a)}</Badge>
  </button></li>;
  return <div className="ex-stack-lg">
    {next ? <CardSurface asChild lift padding="lg"><button type="button" className="ex-next" onClick={() => openAppt(next)}>
      <span className="ex-kicker">Prochain rendez-vous</span>
      <span className="ex-row"><Coin kind={serviceById(next.service).coin} size="lg" /><span className="ex-row-text"><b className="ex-next-title">{serviceById(next.service).name}</b>
        <span className="ex-strong">{whenLabel(next)}</span><span>{serviceById(next.service).mode} · {paymentLabel(next)}</span></span></span>
    </button></CardSurface> : <p className="ex-muted">Aucun rendez-vous à venir.</p>}

    <Section title="Réserver une consultation" aside={<span className="ex-small">Solde par type de pièce</span>}>
      <div className="ex-grid">{SERVICES.map(sv => { const b = s.balances[sv.coin]; return <CardSurface key={sv.id} padding="md" className="ex-service">
        <div className="ex-row"><Coin kind={sv.coin} size="lg" /><div className="ex-row-text"><b>{sv.name}</b><span>{sv.mode} · {sv.duration} min</span></div></div>
        <p className="ex-small">{b} {b > 1 ? sv.coinPlural : sv.coinName} · ou {sv.price} €</p>
        <Button size="sm" variant={b > 0 ? "primary" : "secondary"} onClick={() => book(sv.id)}>Réserver</Button>
      </CardSurface>; })}</div>
    </Section>

    {upcoming.length > 1 || past.length ? <Section title="Mes rendez-vous">
      {upcoming.length > 1 ? <><h3 className="ex-h3">Ensuite</h3><ul className="ex-list">{upcoming.slice(1).map(row)}</ul></> : null}
      {past.length ? <><h3 className="ex-h3">Passés et annulés</h3><ul className="ex-list">{past.map(row)}</ul></> : null}
    </Section> : null}
  </div>;
}

function GeniusSessionDetail({ session }: { session: GeniusSession }) {
  const s = useExchange();
  const [reason, setReason] = useState("");
  const [opened, setOpened] = useState<string | null>(null);
  const unlocked = !!s.geniusCatchup[session.id];
  return <div className="ex-stack">
    <div className="ex-row"><Badge tone="accent">{session.theme}</Badge>{session.missed ? <Badge tone="warning">Manquée</Badge> : <Badge tone="success">Participation reconnue</Badge>}</div>
    <p className="ex-lead">{capitalize(longDate(session.date))}{session.duration ? ` · ${session.duration}` : ""}</p>
    <p>{session.desc}</p>
    {session.missed && !unlocked ? <CardSurface variant="sunken" padding="md" className="ex-stack-sm">
      <p className="ex-strong">Rattrapage</p>
      <p className="ex-small">{GENIUS_CATCHUP_POLICY}</p>
      {session.homework ? <p className="ex-small">Devoir de la séance : {session.homework}</p> : null}
      <Textarea label="Motif" rows={2} value={reason} onChange={e => setReason(e.target.value)} placeholder="Une phrase suffit" />
      <Button disabled={!reason.trim()} onClick={() => requestCatchup(session.id, reason)}>Envoyer un justificatif</Button>
    </CardSurface> : null}
    {session.missed && unlocked ? <Alert tone="success" title="Replay de cette séance ouvert">Justificatif accepté automatiquement dans ce scénario. Seule cette séance est concernée.</Alert> : null}
    {!session.missed || unlocked ? <Button variant="secondary" onClick={() => setOpened("replay")}>Voir le replay</Button> : null}
    {session.resources.length ? <Section title="Ressources"><ul className="ex-list">{session.resources.map(r => <li key={r.id}>
      <button type="button" className="ex-row-button" onClick={() => setOpened(r.id)}><span className="ex-row-text"><b>{r.title}</b><span>{r.format}</span></span></button>
    </li>)}</ul></Section> : null}    {session.leads.length ? <Section title="Pistes d’action"><ul className="ex-list">{session.leads.map(l => { const added = !!s.geniusLeads[l.id]; return <li key={l.id} className="ex-lead-row">
      <span>{l.title}</span>
      <Button size="sm" variant={added ? "ghost" : "secondary"} onClick={() => toggleLead(l.id)}>{added ? "Ajoutée à mes actions" : "Ajouter à mes actions"}</Button>
    </li>; })}</ul></Section> : null}
  </div>;
}

function GeniusTab({ openPeek }: PageProps) {
  const s = useExchange();
  const [question, setQuestion] = useState("");
  const send = () => {
    if (!question.trim()) return;
    askQuestion(question);
    setQuestion("");
  };
  return <div className="ex-stack-lg">
    <CardSurface padding="lg" className="ex-genius-next">
      <span className="ex-kicker">Prochaine séance · {GENIUS_NEXT.theme}</span>
      <h2 className="ex-h2">{GENIUS_NEXT.title}</h2>
      <p className="ex-strong">{capitalize(longDate(GENIUS_NEXT.date))} · {hourLabel(GENIUS_NEXT.start)}–{hourLabel(GENIUS_NEXT.end)} · {TIME_ZONE_LABEL}</p>
      <p>{GENIUS_NEXT.desc}</p>
      <div className="ex-actions"><Button disabled>Rejoindre la visio</Button></div>
      <p className="ex-small">Le bouton s’active de {hourLabel(GENIUS_NEXT.window[0])} à {hourLabel(GENIUS_NEXT.window[1])} le jour de la séance. L’appui dans cette fenêtre vaut participation reconnue.</p>
    </CardSurface>

    <Section title="Questions pour la séance" aside={<span className="ex-small">{s.geniusQuestions.length} questions</span>}>
      <ul className="ex-list">{s.geniusQuestions.map(q => <li key={q.id} className="ex-question">
        <Avatar size="sm" name={q.author} /><div className="ex-row-text"><b>{q.author} <span className="ex-muted">· {q.time}</span></b><span>{q.text}</span></div>
      </li>)}</ul>
      <Composer value={question} onChange={setQuestion} onSend={send} placeholder="Ta question pour la séance…" />
    </Section>

    <Section title="Séances passées">
      <div className="ex-grid">{GENIUS_PAST.map(p => <Card key={p.id} title={p.title} description={p.desc}
        meta={`${capitalize(longDate(p.date))}${p.duration ? ` · ${p.duration}` : ""}`} status={p.missed ? (s.geniusCatchup[p.id] ? "Replay ouvert" : "Manquée · rattrapage possible") : "Participation reconnue"}
        action={<Button size="sm" variant="secondary" onClick={() => openPeek({ title: p.title, content: <GeniusSessionDetail session={p} /> })}>Ouvrir</Button>} />)}</div>
    </Section>
  </div>;
}

function BilanArchiveView({ answers, fact }: { answers: Record<string, string>; fact?: { label: string; text: string } }) {
  return <div className="ex-stack">
    {BILAN_QUESTIONS.map(q => <div key={q.id} className="ex-stack-sm"><p className="ex-kicker">{q.block}</p><p>{answers[q.id] || "Sans réponse"}</p></div>)}
    {fact ? <CardSurface variant="sunken" padding="sm"><p className="ex-small">Repère issu d’Objectifs · {fact.label}</p><p className="ex-strong">{fact.text}</p></CardSurface> : null}
    <p className="ex-small">{BILAN_VERSION}. Aucun score ni synthèse automatique.</p>
  </div>;
}

function BilansTab({ openPeek }: PageProps) {
  const s = useExchange();
  const [answering, setAnswering] = useState(false);
  const [index, setIndex] = useState(0);
  const answered = BILAN_QUESTIONS.filter(q => (s.bilanAnswers[q.id] ?? "").trim()).length;
  const q = BILAN_QUESTIONS[index];
  const setAnswer = (id: string, v: string) => update(st => ({ ...st, bilanAnswers: { ...st.bilanAnswers, [id]: v } }));
  return <div className="ex-stack-lg">
    <CardSurface padding="lg" className="ex-stack">
      <span className="ex-kicker">{BILAN_CURRENT.period}</span>
      <h2 className="ex-h2">{BILAN_CURRENT.title}</h2>
      <p className="ex-muted">Proposé du {longDate(BILAN_CURRENT.from)} au {longDate(BILAN_CURRENT.to)} · {s.bilanSent ? `envoyé le ${longDate(s.bilanSent)}` : `${answered} réponse${answered > 1 ? "s" : ""} sur ${BILAN_QUESTIONS.length}`}</p>
      {s.bilanSent ? <><Alert tone="success" title="Bilan envoyé à l’équipe" /><BilanArchiveView answers={s.bilanAnswers} fact={BILAN_CURRENT.fact} /></>
        : !answering ? <div className="ex-actions"><Button onClick={() => { setAnswering(true); setIndex(Math.max(0, BILAN_QUESTIONS.findIndex(x => !(s.bilanAnswers[x.id] ?? "").trim()))); }}>{answered ? "Reprendre" : "Répondre"}</Button></div>
        : q ? <div className="ex-stack">
          <p className="ex-small">Question {index + 1} sur {BILAN_QUESTIONS.length} · {q.block}</p>
          <p className="ex-lead">{q.text}</p>
          {q.kind === "text" ? <Textarea aria-label={q.text} rows={3} value={s.bilanAnswers[q.id] ?? ""} onChange={e => setAnswer(q.id, e.target.value)} placeholder={q.placeholder} />
            : <SegmentedControl label={q.text} options={q.options.map(o => ({ value: o, label: o }))} value={s.bilanAnswers[q.id] ?? ""} onValueChange={v => setAnswer(q.id, v)} />}
          <div className="ex-actions">
            <Button variant="ghost" onClick={() => index === 0 ? setAnswering(false) : setIndex(index - 1)}>{index === 0 ? "Plus tard" : "Précédent"}</Button>
            {index < BILAN_QUESTIONS.length - 1 ? <Button onClick={() => setIndex(index + 1)}>Suivant</Button>
              : <Button disabled={answered < BILAN_QUESTIONS.length} onClick={() => update(st => ({ ...st, bilanSent: DEMO_TODAY }))}>Envoyer le bilan</Button>}
          </div>
          {index === BILAN_QUESTIONS.length - 1 && answered < BILAN_QUESTIONS.length ? <p className="ex-small">Réponds aux {BILAN_QUESTIONS.length} questions pour envoyer.</p> : null}
        </div> : null}
    </CardSurface>
    <Section title="Bilans précédents">
      <ul className="ex-list">{BILAN_HISTORY.map(b => <li key={b.id}><button type="button" className="ex-row-button" onClick={() => openPeek({ title: b.title, content: <BilanArchiveView answers={b.answers} fact={b.fact} /> })}>
        <span className="ex-row-text"><b>{b.title}</b><span>Envoyé le {longDate(b.sent)}</span></span><Badge size="sm">{b.answers.energy ? `Énergie ${b.answers.energy.toLowerCase()}` : "Envoyé"}</Badge>
      </button></li>)}</ul>
    </Section>
  </div>;
}

function TeamTab({ navigate, book }: PageProps & { book: (service: ServiceId) => void }) {
  return <div className="ex-grid ex-grid-people">{TEAM_PEOPLE.map(p => <CardSurface key={p.id} padding="none" className="ex-person">
    {p.photo ? <img className="ex-person-photo" src={p.photo} alt="" /> : <div className="ex-person-mark"><Avatar size="xl" name={p.name} src={p.avatar} /></div>}
    <div className="ex-person-body">
      <b>{p.name}</b><span className="ex-muted">{p.role}</span><p className="ex-small">{p.does}</p>
      {p.service ? <Button size="sm" variant="secondary" onClick={() => book(p.service!)}>Réserver</Button>
        : <Button size="sm" variant="secondary" onClick={() => navigate("team")}>Écrire à l’équipe</Button>}
    </div>
  </CardSurface>)}</div>;
}

function CoachingPage(props: PageProps) {
  const s = useExchange();
  const book = (service: ServiceId) => {
    update(st => ({ ...st, coachingTab: "consult" }));
    if (props.route !== "accompagnement") props.navigate("accompagnement");
    props.openPeek({ title: `Réserver · ${serviceById(service).name}`, content: <BookingFlow serviceId={service} /> });
  };
  const tabs: { value: CoachingTab; label: string }[] = [
    { value: "calendar", label: "Calendrier" }, { value: "consult", label: "Consultations" }, { value: "genius", label: "Team Genius" }, { value: "bilans", label: "Bilans" }, { value: "team", label: "Équipe" },
  ];
  return <div className="ex-page">
    <Tabs value={s.coachingTab} onValueChange={v => update(st => ({ ...st, coachingTab: v as CoachingTab }))}>
      <TabsList aria-label="Coaching">{tabs.map(t => <TabsTrigger key={t.value} value={t.value}>{t.label}</TabsTrigger>)}</TabsList>
      <TabsContent value="calendar"><div className="ex-tab"><CalendarTab {...props} book={book} /></div></TabsContent>
      <TabsContent value="consult"><div className="ex-tab"><ConsultTab {...props} book={book} /></div></TabsContent>
      <TabsContent value="genius"><div className="ex-tab"><GeniusTab {...props} /></div></TabsContent>
      <TabsContent value="bilans"><div className="ex-tab"><BilansTab {...props} /></div></TabsContent>
      <TabsContent value="team"><div className="ex-tab"><TeamTab {...props} book={book} /></div></TabsContent>
    </Tabs>
  </div>;
}

// ── Communauté ───────────────────────────────────────────────────────────────

const author = (id: string) => COMMUNITY_AUTHORS[id] ?? { id, name: "Membre" };
const spaceOf = (id: string) => SPACES.find(sp => sp.id === id);
const canSee = (space: string, membership: Record<string, boolean>) => { const sp = spaceOf(space); return !!sp && (!sp.optional || !!membership[space]); };
const commentCount = (p: CommunityPost) => p.comments.reduce((n, c) => n + 1 + c.replies.length, 0);
/** Non lu : publication d’un autre auteur pas encore ouverte. Ses propres publications sont toujours lues. */
const isUnread = (p: CommunityPost, read: Record<string, boolean>) => p.author !== "me" && !read[p.id];
const markRead = (id: string) => { if (!state.readPosts[id]) update(st => ({ ...st, readPosts: { ...st.readPosts, [id]: true } })); };
/** Non-lus du fil par espace (l’épinglé est hors fil). */
function unreadBySpace(st: ExchangeState) {
  const counts: Record<string, number> = {};
  for (const p of st.posts) if (p.id !== PINNED_POST_ID && isUnread(p, st.readPosts)) counts[p.space] = (counts[p.space] ?? 0) + 1;
  return counts;
}
function UnreadCount({ n }: { n: number }) {
  return n ? <Badge size="sm" tone="accent" className="ex-unread">{n}<span className="tc-sr-only"> non lu{n > 1 ? "s" : ""}</span></Badge> : null;
}

function Paragraphs({ text }: { text: string }) {
  return <>{text.split(/\n+/).map((t, i) => <p key={i}>{t}</p>)}</>;
}

function PostBody({ post }: { post: CommunityPost }) {
  return <>
    {post.title ? <h2 className="ex-post-title">{post.title}</h2> : null}
    <div className="ex-post-text"><Paragraphs text={post.text} /></div>
    {post.event ? <div className="ex-community-event-poster"><img src={eventVisual("live")} alt={post.event.kicker} loading="lazy"/></div> : null}
    {post.event ? <div className="ex-post-event">
      <img className="ex-event-thumb" src={eventVisual("live")} alt="" loading="lazy" />
      <div className="ex-stack-sm"><span className="ex-kicker">{post.event.kicker} · {post.event.day} {post.event.month}</span><Facts rows={post.event.rows.map(r => [r.k, r.v] as [string, string])} /></div>
    </div> : null}
    {post.challenge ? <CardSurface variant="sunken" padding="sm" className="ex-post-challenge">
      <span className="ex-kicker">{post.challenge.kicker}</span><b>{post.challenge.title}</b>
      <span className="ex-small">Avec {post.challenge.people.map(p => author(p).name).join(", ")}</span>
    </CardSurface> : null}
  </>;
}

const SPACE_STYLE: Record<string, { icon: typeof Trophy; tone: BadgeTone }> = {
  news: {icon:Newspaper,tone:"info"}, events:{icon:CalendarDays,tone:"warning"}, contracts:{icon:Trophy,tone:"success"},
  challenges:{icon:Sparkles,tone:"accent"},results:{icon:TrendingUp,tone:"success"},innovations:{icon:Lightbulb,tone:"info"}
};
function PostHeader({ post, unread }: { post: CommunityPost; unread?: boolean }) {
  const a = author(post.author);
  const kind = SPACE_STYLE[post.space] ?? SPACE_STYLE.news;
  const Glyph = kind.icon;
  return <div className="ex-post-head">
    <Avatar size="md" name={a.name} src={a.avatar} />
    <div className="ex-row-text"><b>{a.name}</b><span>{post.time}</span></div>
    {unread ? <Badge size="sm" tone="accent">Nouveau</Badge> : null}
    <Badge size="sm" tone={kind.tone} icon={<Glyph size={13} />} className={`ex-topic ex-topic-${post.space}`}>{spaceOf(post.space)?.chip}</Badge>
  </div>;
}

function ReactButton({ post }: { post: CommunityPost }) {
  const s = useExchange();
  const mine = !!s.reacted[post.id];
  return <Button size="sm" variant="ghost" className="ex-like" aria-label={`${mine ? "Retirer mon like" : "Aimer"} · ${post.reactions + (mine ? 1 : 0)} likes`} aria-pressed={mine} onClick={() => update(st => ({ ...st, reacted: { ...st.reacted, [post.id]: !mine } }))}>
    <Heart size={18} strokeWidth={1.7} fill={mine ? "currentColor" : "none"} aria-hidden="true" /><span>{post.reactions + (mine ? 1 : 0)}</span>
  </Button>;
}

function CommentView({ comment, reply }: { comment: CommunityComment | CommunityComment["replies"][number]; reply?: boolean }) {
  const a = author(comment.author);
  return <div className={reply ? "ex-comment ex-comment-reply" : "ex-comment"}>
    <Avatar size="sm" name={a.name} src={a.avatar} />
    <div className="ex-row-text"><b>{a.name} <span className="ex-muted">· {comment.time}</span></b>
      <span>{"mention" in comment && comment.mention ? <span className="ex-mention">{comment.mention} </span> : null}{comment.text}</span></div>
  </div>;
}

function PostDetail({ id }: { id: string }) {
  const s = useExchange();
  const post = s.posts.find(p => p.id === id);
  const [draft, setDraft] = useState("");
  if (!post) return <p className="ex-muted">Cette publication n’est plus disponible.</p>;
  const send = () => {
    const text = draft.trim(); if (!text) return;
    update(st => ({ ...st, seq: st.seq + 1, posts: st.posts.map(p => p.id === id ? { ...p, comments: [...p.comments, { id: "mc" + st.seq, author: "me", time: "à l’instant", text, reactions: 0, replies: [] }] } : p) }));
    setDraft("");
  };
  return <div className="ex-stack">
    <PostHeader post={post} />
    <PostBody post={post} />
    <div className="ex-actions ex-actions-start"><ReactButton post={post} /></div>
    <Section title={`${commentCount(post)} commentaire${commentCount(post) > 1 ? "s" : ""}`}>
      {post.comments.length ? <div className="ex-stack">{post.comments.map(c => <div key={c.id} className="ex-stack-sm">
        <CommentView comment={c} />{c.replies.map(r => <CommentView key={r.id} comment={r} reply />)}
      </div>)}</div> : <p className="ex-muted">Sois la première personne à répondre.</p>}
    </Section>
    <Composer value={draft} onChange={setDraft} onSend={send} placeholder="Répondre…" />  </div>;
}

function SpacesPanel() {
  const s = useExchange();
  const unread = unreadBySpace(s);
  return <div className="ex-stack">
    {SPACES.map(sp => <div key={sp.id} className="ex-space-row">
      <div className="ex-row-text"><b>{sp.name}{canSee(sp.id, s.membership) ? <UnreadCount n={unread[sp.id] ?? 0} /> : null}</b><span>{sp.desc}</span><span className="ex-small">Publient : {sp.writers}</span></div>
      {sp.optional ? <Switch aria-label={`Rejoindre ${sp.name}`} checked={!!s.membership[sp.id]} onCheckedChange={v => update(st => ({ ...st, membership: { ...st.membership, [sp.id]: v } }))} />
        : <Badge size="sm">Inclus</Badge>}
    </div>)}
    <p className="ex-small">Les espaces volontaires n’apparaissent dans ton fil qu’une fois rejoints.</p>
  </div>;
}

function ComposePanel() {
  const s = useExchange();
  const writable = SPACES.filter(sp => !sp.teamOnly && canSee(sp.id, s.membership));
  const [space, setSpace] = useState(writable[0]?.id ?? "");
  const [text, setText] = useState("");
  const [done, setDone] = useState<string | null>(null);
  const send = () => {
    const body = text.trim(); if (!body || !space) return;
    update(st => ({ ...st, seq: st.seq + 1, posts: [{ id: "mp" + st.seq, space, author: "me", time: "à l’instant", text: body, reactions: 0, comments: [] }, ...st.posts] }));
    setText(""); setDone(spaceOf(space)?.name ?? "");
  };
  return <div className="ex-stack">
    <SegmentedControl label="Espace" options={writable.map(sp => ({ value: sp.id, label: sp.chip }))} value={space} onValueChange={setSpace} />
    <Composer value={text} onChange={setText} onSend={send} placeholder={SPACE_PLACEHOLDER[space] ?? "Ce que tu veux partager…"} />
    <p className="ex-small">Jamais le nom d’un client, un montant ou un document.</p>
    {done ? <Alert tone="success" title={`Publié dans ${done}`} onDismiss={() => setDone(null)}>Visible en tête du fil.</Alert> : null}
  </div>;
}

function CommunityPage({ openPeek }: PageProps) {
  const s = useExchange();
  const [filter, setFilter] = useState("all");
  const visibleSpaces = SPACES.filter(sp => canSee(sp.id, s.membership));
  const effective = filter !== "all" && !visibleSpaces.some(sp => sp.id === filter) ? "all" : filter;
  const pinned = s.posts.find(p => p.id === PINNED_POST_ID);
  const feed = s.posts.filter(p => p.id !== PINNED_POST_ID && canSee(p.space, s.membership) && (effective === "all" || p.space === effective));
  const unread = unreadBySpace(s);
  useEffect(()=>{setPageChrome({compose:()=>openPeek({title:"Écrire une publication",content:<ComposePanel/>})});return()=>setPageChrome({});},[openPeek]);
  const unreadAll = visibleSpaces.reduce((n, sp) => n + (unread[sp.id] ?? 0), 0);
  const openPost = (p: CommunityPost) => { markRead(p.id); openPeek({ title: spaceOf(p.space)?.name ?? "Publication", content: <PostDetail id={p.id} /> }); };
  return <div className="ex-page ex-feed-page">
    <div className="ex-toolbar">
      <SegmentedControl size="sm" label="Filtrer le fil" value={effective} onValueChange={setFilter} options={[
        { value: "all", label: "Tout", accessory: <UnreadCount n={unreadAll} /> },
        ...visibleSpaces.map(sp => ({ value: sp.id, label: sp.chip, accessory: <UnreadCount n={unread[sp.id] ?? 0} /> })),
      ]} />
      <div className="ex-actions">
      </div>
    </div>
    {pinned && !s.pinnedClosed ? <Alert tone="info" title={pinned.title ?? "Épinglé"} onDismiss={() => update(st => ({ ...st, pinnedClosed: true }))}>
      <Paragraphs text={pinned.text} />
    </Alert> : null}
    <div className="ex-feed">{feed.map(p => <CardSurface key={p.id} padding="md" className="ex-post">
      <article className="ex-stack-sm">
        <PostHeader post={p} unread={isUnread(p, s.readPosts)} />
        <PostBody post={p} />
        <div className="ex-post-foot"><ReactButton post={p} />
          <Button size="sm" variant="ghost" aria-label={`${commentCount(p)} commentaire${commentCount(p) > 1 ? "s" : ""}. Ouvrir la discussion`} onClick={() => openPost(p)}><MessageCircle size={18} strokeWidth={1.7} aria-hidden="true" /><span>{commentCount(p)}</span></Button></div>
      </article>
    </CardSurface>)}
    {!feed.length ? <p className="ex-muted">Aucune publication dans cet espace pour l’instant.</p> : null}</div>
  </div>;
}

// ── Messages avec l’équipe ──────────────────────────────────────────────────

const CHAT_PARTICIPANTS = [{ id: "me", name: MEMBER.name }, { id: "team", name: TEAM_AUTHOR.name, avatar: TEAM_AUTHOR.avatar }];
const CHAT_LABELS = {
  today: "Aujourd’hui", yesterday: "Hier", unknownAuthor: "Inconnu", jumpToLatest: "Aller au dernier message",
  newMessages: (n: number) => `${n} nouveau${n > 1 ? "x" : ""} message${n > 1 ? "s" : ""}`, addReaction: "Ajouter une réaction", reactions: "Réactions",
  reactWith: (e: string) => `Réagir avec ${e}`, reactionCount: (e: string, n: number, you: boolean) => `${e} ${n}${you ? ", dont toi" : ""}`,
  readBy: (names: string[]) => `Lu par ${names.join(", ")}`, typing: (names: string[]) => `${names[0]} écrit…`, retry: "Réessayer", failed: "Non distribué",
  sending: "Envoi", sent: "Envoyé", delivered: "Distribué", read: "Lu", send: "Envoyer",
};

function TeamMessagesPage(_: PageProps) {
  const s = useExchange();
  const narrow = useNarrow();
  const [showList, setShowList] = useState(true);
  const [draft, setDraft] = useState("");
  const thread = s.threads.find(t => t.id === s.activeThread) ?? s.threads[0];
  const messages: ChatMessage[] = useMemo(() => (thread?.messages ?? []).map(m => ({ id: m.id, authorId: m.from, text: m.text, createdAt: m.at })), [thread]);
  const select = (id: string) => { update(st => ({ ...st, activeThread: id, threads: st.threads.map(t => t.id === id ? { ...t, unread: false } : t) })); setShowList(false); };
  const send = () => {
    const text = draft.trim(); if (!text || !thread) return;
    update(st => ({ ...st, seq: st.seq + 1, threads: st.threads.map(t => t.id === thread.id
      ? { ...t, when: "à l’instant", messages: [...t.messages, { id: "mm" + st.seq, from: "me", at: new Date(DEMO_NOW.getTime() + st.seq * 60_000).toISOString(), text }] } : t) }));
    setDraft("");
  };
  const newThread = () => {
    update(st => ({ ...st, seq: st.seq + 1, activeThread: "nt" + st.seq, threads: [{ id: "nt" + st.seq, subject: "Nouvelle conversation", when: "à l’instant", messages: [] }, ...st.threads] }));
    setShowList(false);
  };
  useEffect(()=>{setPageChrome({title:narrow&&!showList?thread?.subject:undefined,back:narrow&&!showList?()=>setShowList(true):undefined,compose:showList?newThread:undefined});return()=>setPageChrome({});},[narrow,showList,thread?.id]);
  const listHidden = narrow && !showList, paneHidden = narrow && showList;
  return <div className="ex-messages">
    <nav className="ex-thread-list" aria-label="Conversations" hidden={listHidden}>
      <ul className="ex-list">{s.threads.map(t => <li key={t.id}><button type="button" className="ex-row-button ex-thread" aria-current={t.id === thread?.id && !narrow ? "true" : undefined} onClick={() => select(t.id)}>
        <Avatar size="md" name={TEAM_AUTHOR.name} src={TEAM_AUTHOR.avatar} />
        <span className="ex-row-text"><b>{t.subject}</b><span>{t.messages[t.messages.length - 1]?.text ?? "Aucun message"}</span></span>
        <span className="ex-thread-meta"><span className="ex-small">{t.when}</span>{t.unread ? <Badge size="sm" tone="accent">1<span className="tc-sr-only"> non lu</span></Badge> : null}</span>
      </button></li>)}</ul>
    </nav>
    {thread ? <section className="ex-thread-pane" aria-label={thread.subject} hidden={paneHidden}>
      <div className="ex-thread-head" hidden={narrow}>
        <div className="ex-row-text"><b>{thread.subject}</b><span>{thread.context ?? "Équipe Team Coaching"}</span></div>
      </div>
      <div className="ex-thread-body">
        {messages.length ? <ChatThread participants={CHAT_PARTICIPANTS} currentUserId="me" messages={messages} composer={false} locale="fr-FR" label={`Conversation : ${thread.subject}`} labels={CHAT_LABELS} />
          : <p className="ex-muted ex-thread-empty">Écris ton premier message : l’équipe répond ici.</p>}
      </div>
      <div className="ex-thread-composer">
        <Composer value={draft} onChange={setDraft} onSend={send} placeholder="Écrire à l’équipe…" />      </div>
    </section> : null}
  </div>;
}

// ── Boutique ─────────────────────────────────────────────────────────────────

function OfferVisual({ cover, photo }: { cover: OfferCover; photo?: Offer["photo"] }) {
  if (photo) return <img className="ex-offer-photo" src={photo.src} alt="" style={{ objectPosition: photo.position }} />;
  if (cover === "silver" || cover === "gold" || cover === "black" || cover === "red") return <span className={`ex-offer-art ex-art-coin`}><Coin kind={cover} size="lg" /></span>;
  const art: Record<Exclude<OfferCover, CoinKind>, ReactNode> = {
    formation: <svg viewBox="0 0 320 180" aria-hidden="true"><text x="26" y="40" fontSize="13" fontWeight="600" letterSpacing="2" fill="currentColor" opacity=".55">NIVEAU 2</text><rect x="132" y="118" width="52" height="40" rx="3" fill="currentColor" opacity=".28" /><rect x="188" y="90" width="52" height="68" rx="3" fill="currentColor" opacity=".55" /><rect x="244" y="58" width="52" height="100" rx="3" fill="currentColor" /><path d="M270 58V30" stroke="currentColor" strokeWidth="3" /><path d="M270 30h18l-5 6 5 6h-18" fill="currentColor" /><rect x="26" y="70" width="84" height="8" rx="4" fill="currentColor" opacity=".55" /><rect x="26" y="86" width="64" height="8" rx="4" fill="currentColor" opacity=".3" /><rect x="26" y="102" width="74" height="8" rx="4" fill="currentColor" opacity=".3" /></svg>,
    tarifs: <svg viewBox="0 0 320 200" aria-hidden="true"><g transform="rotate(-5 140 104)"><rect x="58" y="22" width="164" height="164" rx="6" fill="currentColor" opacity=".1" /><rect x="76" y="40" width="74" height="10" rx="5" fill="currentColor" /><rect x="76" y="56" width="46" height="6" rx="3" fill="currentColor" opacity=".35" /><rect x="76" y="76" width="128" height="88" rx="3" fill="none" stroke="currentColor" strokeOpacity=".55" strokeWidth="1.5" /><path d="M118 76v88M160 76v88M76 98h128M76 120h128M76 142h128" stroke="currentColor" strokeOpacity=".55" strokeWidth="1.5" /></g><g transform="rotate(16 250 122)"><path d="M206 98h56l24 24-24 24h-56a6 6 0 0 1-6-6v-36a6 6 0 0 1 6-6z" fill="currentColor" /><text x="232" y="133" textAnchor="middle" fontSize="30" fontWeight="600" fill="var(--panel)">€</text></g></svg>,
    atelier: <svg viewBox="0 0 320 180" aria-hidden="true"><text x="24" y="36" fontSize="12" fontWeight="600" letterSpacing="2" fill="currentColor" opacity=".6">ATELIER</text><rect x="60" y="48" width="200" height="104" rx="8" fill="none" stroke="currentColor" strokeWidth="3" /><rect x="76" y="62" width="46" height="36" rx="3" fill="currentColor" /><rect x="132" y="62" width="46" height="36" rx="3" fill="currentColor" opacity=".45" /><rect x="188" y="62" width="46" height="36" rx="3" fill="currentColor" opacity=".25" /><rect x="104" y="106" width="46" height="34" rx="3" fill="currentColor" opacity=".6" /><rect x="160" y="106" width="46" height="34" rx="3" fill="currentColor" opacity=".35" /></svg>,
    audit: <svg viewBox="0 0 320 180" aria-hidden="true"><rect x="58" y="26" width="150" height="128" rx="10" fill="currentColor" opacity=".08" /><rect x="58" y="26" width="150" height="128" rx="10" fill="none" stroke="currentColor" strokeWidth="2.5" /><rect x="76" y="48" width="16" height="16" rx="3" fill="currentColor" /><rect x="102" y="52" width="84" height="8" rx="4" fill="currentColor" opacity=".55" /><rect x="76" y="80" width="16" height="16" rx="3" fill="none" stroke="currentColor" strokeWidth="2.2" /><rect x="102" y="84" width="70" height="8" rx="4" fill="currentColor" opacity=".35" /><rect x="76" y="112" width="16" height="16" rx="3" fill="none" stroke="currentColor" strokeWidth="2.2" /><rect x="102" y="116" width="78" height="8" rx="4" fill="currentColor" opacity=".35" /><circle cx="226" cy="104" r="30" fill="none" stroke="currentColor" strokeWidth="8" /><path d="M248 126l26 26" stroke="currentColor" strokeWidth="10" strokeLinecap="round" /></svg>,
    crm: <svg viewBox="0 0 320 180" aria-hidden="true"><g fill="none" stroke="currentColor" strokeWidth="2" opacity=".5"><rect x="30" y="30" width="76" height="122" rx="8" /><rect x="122" y="30" width="76" height="122" rx="8" /><rect x="214" y="30" width="76" height="122" rx="8" /></g><rect x="38" y="42" width="60" height="22" rx="5" fill="currentColor" opacity=".35" /><rect x="38" y="70" width="60" height="22" rx="5" fill="currentColor" opacity=".35" /><rect x="38" y="98" width="60" height="22" rx="5" fill="currentColor" opacity=".35" /><rect x="130" y="42" width="60" height="22" rx="5" fill="currentColor" opacity=".6" /><rect x="130" y="70" width="60" height="22" rx="5" fill="currentColor" opacity=".6" /><rect x="222" y="42" width="60" height="22" rx="5" fill="currentColor" /></svg>,
    slides: <svg viewBox="0 0 320 180" aria-hidden="true"><rect x="54" y="28" width="212" height="122" rx="8" fill="currentColor" opacity=".08" /><rect x="54" y="28" width="212" height="122" rx="8" fill="none" stroke="currentColor" strokeWidth="2.5" /><rect x="74" y="46" width="96" height="10" rx="5" fill="currentColor" /><rect x="74" y="62" width="64" height="7" rx="3.5" fill="currentColor" opacity=".4" /><rect x="176" y="98" width="16" height="36" rx="2" fill="currentColor" opacity=".35" /><rect x="198" y="82" width="16" height="52" rx="2" fill="currentColor" opacity=".6" /><rect x="220" y="62" width="16" height="72" rx="2" fill="currentColor" /><rect x="74" y="98" width="80" height="7" rx="3.5" fill="currentColor" opacity=".4" /><rect x="74" y="112" width="68" height="7" rx="3.5" fill="currentColor" opacity=".4" /><path d="M160 150v12M140 166h40" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" /></svg>,
  };
  return <span className={`ex-offer-art ex-art-${cover}`}>{art[cover as Exclude<OfferCover, CoinKind>]}</span>;
}

type OfferStatus = { tone: BadgeTone; chip: string; line: string } | null;
function offerStatus(o: Offer, st: ExchangeState): OfferStatus {
  if (o.mode === "not_included") return { tone: "neutral", chip: "Non inclus", line: "Non inclus dans ton accompagnement actuel" };
  const acq = st.acquisitions.find(a => a.offer === o.id), req = st.requests.find(r => r.offer === o.id);
  if (acq?.state === "running") return { tone: "info", chip: "En cours", line: `Acquise le ${longDate(acq.date)} · prestation en cours, pas encore réalisée` };
  if (acq) return { tone: "success", chip: "Acquis", line: `Acquis le ${longDate(acq.date)} · ${PAY_LABEL[acq.pay].label}` };
  if (req) return { tone: "info", chip: "Demande envoyée", line: `Demande enregistrée le ${longDate(req.date)} · en attente de réponse de l’équipe` };
  if (o.service) { const sv = serviceById(o.service), b = st.balances[sv.coin]; return { tone: b > 0 ? "success" : "neutral", chip: b > 0 ? `${b} ${b > 1 ? sv.coinPlural : sv.coinName}` : "À réserver", line: `${b} ${b > 1 ? sv.coinPlural + " disponibles" : sv.coinName + " disponible"} · ou ${sv.price} € à la réservation` }; }
  return null;
}
const offerMeta = (o: Offer) => o.mode === "not_included" ? "Non inclus dans ton accompagnement" : o.mode === "quote" ? "Sur devis" : o.mode === "buy" ? `Achat · ${o.pay!.map(p => PAY_LABEL[p].label).join(" ou ")}` : "Pièce ou euros, à la réservation";

function OfferDetail({ id, onBook, navigate }: { id: string; onBook: (service: ServiceId) => void; navigate: (route: Route) => void }) {
  const s = useExchange();
  const o = OFFERS.find(x => x.id === id)!;
  const status = offerStatus(o, s);
  const [pay, setPay] = useState<PayMethod | null>(o.pay?.length === 1 ? o.pay[0] : null);
  const [request, setRequest] = useState("");
  const acquired = s.acquisitions.find(a => a.offer === o.id);
  const requested = s.requests.find(r => r.offer === o.id);
  return <div className="ex-stack">
    <div className="ex-offer-hero"><OfferVisual cover={o.cover} photo={o.photo} /></div>
    <p className="ex-kicker">{o.kicker}</p>
    <p className="ex-lead">{o.benefit}</p>
    <ul className="ex-bullets">{o.content.map(c => <li key={c}>{c}</li>)}</ul>
    <Facts rows={o.conditions} />
    {status ? <p className="ex-muted" role="status">{status.line}</p> : null}

    {o.mode === "not_included" ? <Alert tone="info" title="Non inclus dans ton accompagnement actuel">Aucun achat n’est proposé pour ce niveau. L’équipe peut t’en parler depuis Messages.</Alert> : null}
    {(o.mode === "credit" || o.mode === "book") && o.service ? <div className="ex-actions"><Button onClick={() => onBook(o.service!)}>Réserver</Button></div> : null}
    {o.mode === "buy" && !acquired ? <div className="ex-stack-sm">
      <RadioCards aria-label="Règlement" value={pay} onValueChange={v => setPay(v as PayMethod)} options={o.pay!.map(p => ({ value: p, label: PAY_LABEL[p].label, description: PAY_LABEL[p].sub }))} />
      <Button disabled={!pay} onClick={() => acquireOffer({ offerId: o.id, pay: pay! })}>Confirmer l’acquisition</Button>
      <p className="ex-small">Un seul mode par acquisition.</p>
    </div> : null}
    {o.mode === "buy" && acquired ? <Alert tone="success" title="Acquis">{o.opens ? "" : "L’équipe te contacte pour la réalisation."}</Alert> : null}
    {acquired && o.opens && acquired.state === "access" ? <div className="ex-actions"><Button onClick={() => navigate(o.opens!.route)}>{o.opens.label}</Button></div> : null}
    {o.mode === "quote" && !requested ? <div className="ex-stack-sm">
      <Textarea label="Ta demande" rows={3} value={request} onChange={e => setRequest(e.target.value)} placeholder="Ce que tu attends, ta situation actuelle…" />
      <Button disabled={!request.trim()} onClick={() => requestQuote({ offerId: o.id, text: request })}>Demander un devis</Button>
      <p className="ex-small">Une demande ne débite rien et ne donne encore aucun droit.</p>
    </div> : null}
    {o.mode === "quote" && requested ? <CardSurface variant="sunken" padding="sm"><p className="ex-small">Ta demande</p><p>{requested.text}</p></CardSurface> : null}
  </div>;
}

function OfferCard({ offer, onOpen }: { offer: Offer; onOpen: () => void }) {
  const s = useExchange();
  const status = offerStatus(offer, s);
  return <CardSurface asChild lift padding="none" className="ex-offer">
    <button type="button" onClick={onOpen} aria-label={`${offer.title}. ${offer.kicker}. ${status ? status.chip + ". " : ""}${offerMeta(offer)}`}>
      <OfferVisual cover={offer.cover} photo={offer.photo} />
      <span className="ex-offer-overlay">
        {status ? <Badge size="sm" tone={status.tone}>{status.chip}</Badge> : null}
        <span className="ex-offer-kicker">{offer.kicker}</span>
        <span className="ex-offer-title">{offer.title}</span>
        <span className="ex-offer-meta">{offerMeta(offer)}</span>
      </span>
    </button>
  </CardSurface>;
}

function ShopPage(props: PageProps) {
  const s = useExchange();
  const [category, setCategory] = useState("all");
  const book = (service: ServiceId) => {
    update(st => ({ ...st, coachingTab: "consult" }));
    props.navigate("accompagnement");
    props.openPeek({ title: `Réserver · ${serviceById(service).name}`, content: <BookingFlow serviceId={service} /> });
  };
  const open = (o: Offer) => props.openPeek({ title: o.title, content: <OfferDetail id={o.id} onBook={book} navigate={props.navigate} /> });
  const offers = OFFERS.filter(o => category === "all" || o.category === category);
  const mine = OFFERS.filter(o => s.acquisitions.some(a => a.offer === o.id) || s.requests.some(r => r.offer === o.id));
  const credits = SERVICES.filter(sv => s.balances[sv.coin] > 0);
  return <div className="ex-page ex-stack-lg">
    <SegmentedControl size="sm" label="Catégories" options={SHOP_CATEGORIES} value={category} onValueChange={setCategory} />
    <div className="ex-offer-grid">{offers.map(o => <OfferCard key={o.id} offer={o} onOpen={() => open(o)} />)}</div>
    <Section title="Mes achats et demandes">
      <div className="ex-grid">
        {mine.map(o => { const st = offerStatus(o, s); return <CardSurface key={o.id} asChild lift padding="md"><button type="button" className="ex-mine" onClick={() => open(o)}>
          <span className="ex-row-text"><b>{o.title}</b><span>{st?.line}</span></span>{st ? <Badge size="sm" tone={st.tone}>{st.chip}</Badge> : null}
        </button></CardSurface>; })}
        {credits.map(sv => <CardSurface key={sv.id} asChild lift padding="md"><button type="button" className="ex-mine" onClick={() => book(sv.id)}>
          <Coin kind={sv.coin} /><span className="ex-row-text"><b>{sv.name}</b><span>{s.balances[sv.coin]} {s.balances[sv.coin] > 1 ? sv.coinPlural : sv.coinName} à réserver</span></span>
        </button></CardSurface>)}
      </div>
    </Section>
  </div>;
}

// ── Compte et Miles ──────────────────────────────────────────────────────────

const milesBalance = () => MILES_JOURNAL.reduce((n, e) => n + (e.kind === "gain" ? e.amount : -e.amount), 0);

function MemberCard({ onMiles }: { onMiles?: () => void }) {
  return <CardSurface padding="lg" className="ex-member-card">
    <img className="ex-member-logo" src={asset("tc-logo-white.png")} alt="Team Coaching" />
    <div className="ex-member-id"><Avatar size="lg" name={MEMBER.name} /><div className="ex-row-text"><b>{MEMBER.name}</b><span>{MEMBERSHIP.label}</span></div></div>
    <div className="ex-member-foot">
      <span className="ex-row-text"><span>Miles disponibles</span><b className="ex-member-miles">{milesBalance()}</b></span>
      {onMiles ? <Button size="sm" variant="secondary" onClick={onMiles}>Historique des Miles</Button> : null}
    </div>
  </CardSurface>;
}

function accountHistory(st: ExchangeState): TimelineEvent[] {
  const appts = st.appointments.map(a => ({ id: "a-" + a.id, at: at(a.date, a.time).getTime(), title: `${a.status === "cancelled" ? "Rendez-vous annulé" : a.status === "done" ? "Rendez-vous passé" : "Rendez-vous réservé"} · ${serviceById(a.service).name}`,
    meta: paymentLabel(a), tone: a.status === "cancelled" ? "neutral" as const : "success" as const }));
  const acq = st.acquisitions.map(a => { const o = OFFERS.find(x => x.id === a.offer); return { id: "q-" + a.offer, at: at(a.date, "12:00").getTime(), title: `Acquisition · ${o?.title}`, meta: `${PAY_LABEL[a.pay].label}`, tone: "success" as const }; });
  const req = st.requests.map(r => { const o = OFFERS.find(x => x.id === r.offer); return { id: "r-" + r.offer, at: at(r.date, "12:00").getTime(), title: `Demande de devis · ${o?.title}`, meta: "En attente de réponse", tone: "neutral" as const }; });
  const bilans = [...BILAN_HISTORY.map(b => ({ id: "b-" + b.id, at: at(b.sent, "09:00").getTime(), title: `${b.title} envoyé`, tone: "neutral" as const })),
    ...(st.bilanSent ? [{ id: "b-cur", at: at(st.bilanSent, "18:00").getTime(), title: `${BILAN_CURRENT.title} envoyé`, tone: "neutral" as const }] : [])];
  return [...appts, ...acq, ...req, ...bilans];
}

function AccountPage({ navigate }: PageProps) {
  const s = useExchange();
  const families = NOTIFICATION_PREFS.filter(p => p.group === "families"), reminders = NOTIFICATION_PREFS.filter(p => p.group === "reminders");
  const toggles = (list: typeof NOTIFICATION_PREFS) => <div className="ex-toggle-list">{list.map(p => <label key={p.id} className="ex-toggle">
    <span className="ex-row-text"><b>{p.label}</b><span>{p.sub}</span></span>
    <Switch checked={!!s.notifications[p.id]} onCheckedChange={v => setNotification(p.id, v)} />
  </label>)}</div>;
  return <div className="ex-page ex-account">
    <div className="ex-account-main">
      <MemberCard onMiles={() => navigate("miles")} />
      <Section title="Profil"><Facts rows={[["Nom", MEMBER.name], ["E-mail", MEMBER.email], ["Accompagnement", MEMBERSHIP.program]]} /></Section>
      <Section title="Raccourcis"><div className="ex-actions ex-actions-start">
        <Button size="sm" variant="secondary" onClick={() => navigate("shop")}>Mes achats</Button>
        <Button size="sm" variant="secondary" onClick={() => navigate("accompagnement")}>Mon calendrier</Button>
        <Button size="sm" variant="secondary" onClick={() => navigate("team")}>Messages</Button>
      </div></Section>
      <Section title="Notifications">{toggles(families)}<h3 className="ex-h3">Rappels d’accompagnement</h3>{toggles(reminders)}<p className="ex-small">{ACCOUNT_NOTE}</p></Section>
    </div>
    <Section title="Historique du compte" className="ex-account-side">
      <Timeline events={accountHistory(s)} now={DEMO_NOW.getTime()} label="Historique du compte" timeZone="Europe/Paris" locale="fr-FR" headingLevel={3} />
    </Section>
  </div>;
}

function MilesPage(props: PageProps) {
  const balance = milesBalance();
  const gained = MILES_JOURNAL.filter(e => e.kind === "gain").reduce((n, e) => n + e.amount, 0);
  const spent = MILES_JOURNAL.filter(e => e.kind === "spend").reduce((n, e) => n + e.amount, 0);
  const events: TimelineEvent[] = MILES_JOURNAL.map(e => ({ id: e.id, at: at(e.date, "09:00").getTime(), title: `${e.kind === "gain" ? "+" : "−"}${e.amount} Miles · ${e.label}`, tone: e.kind === "gain" ? "success" : "neutral" }));
  const milesOffers = OFFERS.filter(o => o.pay?.includes("miles"));
  const book = (service: ServiceId) => { update(st => ({ ...st, coachingTab: "consult" })); props.navigate("accompagnement"); props.openPeek({ title: `Réserver · ${serviceById(service).name}`, content: <BookingFlow serviceId={service} /> }); };
  return <div className="ex-page ex-account">
    <div className="ex-account-main">
      <MemberCard />
      <div className="ex-grid">
        <MetricCard label="Solde" value={balance} suffix=" Miles" context="Au 1er octobre 2026" />
        <MetricCard label="Gagnés" value={gained} suffix=" Miles" context="Depuis juillet 2026" />
        <MetricCard label="Utilisés" value={spent} suffix=" Miles" context="Depuis juillet 2026" />
      </div>
      <p className="ex-small">{MILES_NOTE}</p>
      <Section title="Utiliser tes Miles"><div className="ex-offer-grid ex-offer-grid-compact">{milesOffers.map(o => <OfferCard key={o.id} offer={o}
        onOpen={() => props.openPeek({ title: o.title, content: <OfferDetail id={o.id} onBook={book} navigate={props.navigate} /> })} />)}</div></Section>
    </div>
    <Section title="Historique des Miles" className="ex-account-side">
      <Timeline events={events} now={DEMO_NOW.getTime()} label="Historique des Miles" timeZone="Europe/Paris" locale="fr-FR" headingLevel={3} />
    </Section>
  </div>;
}

// ── Point d’entrée ───────────────────────────────────────────────────────────

export function ExchangePages(props: PageProps) {
  switch (props.route) {
    case "accompagnement": return <CoachingPage {...props} />;
    case "community": return <CommunityPage {...props} />;
    case "team": return <TeamMessagesPage {...props} />;
    case "shop": return <ShopPage {...props} />;
    case "account": return <AccountPage {...props} />;
    case "miles": return <MilesPage {...props} />;
    default: return null;
  }
}

/** Bilans hosted outside Coaching (Agir · Historique): the existing BilansTab on the same Échanges store, no state copy. */
export function BilanExperience(props: PageProps) { return <BilansTab {...props} />; }
/** Current bilan as the store sees it: open when today is inside its proposal window, `sent` once sent (real store value). */
export function useCurrentBilan() {
  const s = useExchange();
  return { id: BILAN_CURRENT.id, title: BILAN_CURRENT.title, from: BILAN_CURRENT.from, to: BILAN_CURRENT.to,
    open: DEMO_TODAY >= BILAN_CURRENT.from && DEMO_TODAY <= BILAN_CURRENT.to, sent: s.bilanSent };
}

export default ExchangePages;
